import type { Incident } from '../lib/types';

interface Props {
  incidents: Incident[] | null;
  visibleLimit?: number;
}

const STATUS_LABEL: Record<string, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
  reopened: 'Re-opened',
};

// "Investigating" doesn't fit planned maintenance windows -- nothing is being
// investigated, the work is just running. Show "Update" instead so successive
// maintenance posts read naturally on the timeline. Mirrors the same rename
// in URadmin's incident detail view.
function statusLabel(status: string, impact: string): string {
  if (impact === 'maintenance' && status === 'investigating') return 'Update';
  return STATUS_LABEL[status] || status;
}

const IMPACT_DOT: Record<string, string> = {
  critical: 'bg-status-major',
  major: 'bg-status-partial',
  minor: 'bg-status-degraded',
  maintenance: 'bg-status-maintenance',
  none: 'bg-[var(--color-fg-subtle)]',
};

function dateHeader(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface GroupedIncidents {
  [date: string]: Incident[];
}

export function IncidentTimeline({ incidents, visibleLimit = 20 }: Readonly<Props>) {
  if (!incidents) {
    return (
      <div className="py-6 text-sm text-[var(--color-fg-muted)]">Loading incident history…</div>
    );
  }

  if (incidents.length === 0) {
    return (
      <div className="py-6 text-sm text-[var(--color-fg-muted)]">
        No incidents reported in the last 90 days.
      </div>
    );
  }

  const visible = incidents.slice(0, visibleLimit);

  const groups: GroupedIncidents = {};
  for (const inc of visible) {
    const key = (inc.resolvedAt || inc.createdAt).slice(0, 10);
    groups[key] ??= [];
    groups[key].push(inc);
  }
  // Sort days descending and, within each day, incidents by most recent
  // activity first. The API already orders by COALESCE(resolved_at, created_at)
  // DESC but we re-sort defensively in case the upstream order ever changes.
  const keys = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));
  for (const k of keys) {
    groups[k].sort((a, b) => {
      const at = new Date(a.resolvedAt || a.createdAt).getTime();
      const bt = new Date(b.resolvedAt || b.createdAt).getTime();
      return bt - at;
    });
  }

  return (
    <div className="space-y-8">
      {keys.map((k) => (
        <div key={k}>
          <h3 className="font-display mb-3 border-b border-[var(--color-border-subtle)] pb-2 text-sm font-medium uppercase tracking-wider text-[var(--color-fg-muted)]">
            {dateHeader(k + 'T00:00:00Z')}
          </h3>
          <ul className="space-y-4">
            {groups[k].map((inc) => (
              <li key={inc.id} className="flex gap-3">
                <span
                  className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                    IMPACT_DOT[inc.impact] || 'bg-[var(--color-fg-subtle)]'
                  }`}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-medium text-[var(--color-fg)]">
                    {inc.title}
                  </div>
                  <ol className="mt-2 space-y-2">
                    {inc.updates.map((u) => (
                      <li key={u.id} className="text-sm">
                        <span className="font-medium text-[var(--color-fg)]">
                          {statusLabel(u.status, inc.impact)}
                        </span>
                        <span className="font-mono text-[var(--color-fg-subtle)]">
                          {' '}
                          · {timeOf(u.createdAt)}
                        </span>
                        <div className="mt-0.5 leading-relaxed text-[var(--color-fg-muted)]">
                          {u.message}
                        </div>
                      </li>
                    ))}
                    {inc.resolvedAt && (
                      <li className="font-mono text-xs text-[var(--color-fg-subtle)]">
                        Resolved · {timeOf(inc.resolvedAt)}
                      </li>
                    )}
                  </ol>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
